// The compact profile popover that opens from the user-card
// avatar. Light grape + white surface with a strawberry banner
// band, status pill row, and status picker.

import { ChevronDown, Edit3, LogOut, Palette, Smile } from "lucide-react";
import { useState } from "react";
import type { ReactNode } from "react";

import {
  AvatarFallback,
  AvatarImage,
  Avatar as AvatarRoot,
  PopoverContent,
  cn,
} from "@link/ui";

import { signOutSession } from "../lib/device-session";
import { clearIdentityCache } from "../lib/identity";
import { StatusDot } from "./StatusDot";
import { ProfileUsername } from "./ProfileUsername";
import { useSelf, visibleStatus, type SelfStatus } from "../state/SelfSession";
import { profileBannerStyle } from "../lib/profile-banner";

const STATUS_ORDER: ReadonlyArray<{
  id: SelfStatus;
  label: string;
  hint: string;
}> = [
  { id: "online", label: "Online", hint: "" },
  { id: "away", label: "Idle", hint: "" },
  { id: "busy", label: "Do not disturb", hint: "Pauses notifications" },
  { id: "invisible", label: "Invisible", hint: "Appear offline" },
];

interface Props {
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  onOpenFullProfile?: () => void;
}

export function ProfilePopover({
  side = "right",
  align = "end",
  onOpenFullProfile,
}: Props) {
  return (
    <PopoverContent
      side={side}
      align={align}
      sideOffset={12}
      collisionPadding={12}
      sticky="always"
      className="!z-[160] max-h-[calc(100dvh-1.5rem)] w-[min(320px,calc(100vw-1.5rem))] overflow-x-hidden overflow-y-auto rounded-2xl border-cloud-300 bg-white p-0 text-cloud-700 shadow-2xl shadow-black/25"
    >
      <SelfProfileCardContent onOpenFullProfile={onOpenFullProfile} />
    </PopoverContent>
  );
}

export function SelfProfileCardContent({
  onOpenFullProfile,
}: {
  onOpenFullProfile?: () => void;
}) {
  const self = useSelf();
  const dotStatus = visibleStatus(self.status);

  return (
    <>
      <div className="relative h-24 overflow-hidden">
        <div
          className="absolute inset-0"
          style={profileBannerStyle(
            self.identity.bannerColor,
            self.identity.bannerMode,
            self.identity.bannerSecondaryColor,
          )}
        />
      </div>

      <div className="-mt-12 flex items-end gap-3 px-4">
        <button
          type="button"
          onClick={onOpenFullProfile}
          className="group relative rounded-full text-left ring-[6px] ring-cloud-50 transition-transform hover:scale-[1.03]"
          aria-label="Open full profile"
        >
          <AvatarRoot className="h-[84px] w-[84px]">
            {self.identity.avatarUrl !== null ? (
              <AvatarImage src={self.identity.avatarUrl} alt="" />
            ) : null}
            <AvatarFallback className="text-2xl font-semibold uppercase">
              {self.identity.name.slice(0, 1)}
            </AvatarFallback>
          </AvatarRoot>
          <span className="absolute bottom-1 right-1 rounded-full bg-cloud-50 p-[3px]">
            <StatusDot
              status={dotStatus}
              sizeClass="h-3.5 w-3.5"
              ringClass="ring-cloud-50"
            />
          </span>
        </button>

        <button
          type="button"
          onClick={() => self.openSettings("profile")}
          className="ui-glass-button min-w-0 flex-1 translate-y-2 rounded-2xl rounded-bl-sm bg-grape-50 px-3 py-2 text-left text-xs italic text-cloud-600 shadow-sm ring-1 ring-grape-100 transition-colors hover:bg-grape-100"
        >
          <span className="mr-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-cloud-200 align-middle text-cloud-600">
            <Smile className="h-2.5 w-2.5" />
          </span>
          <span className={cn("inline-block max-w-full truncate align-middle", self.identity.customStatus === null ? "text-cloud-400" : undefined)}>
            {self.identity.customStatus ?? "Set a custom status"}
          </span>
        </button>
      </div>

      <div className="px-4 pb-1 pt-5">
        <p className="text-lg font-bold text-cloud-800 [overflow-wrap:anywhere]">
          {self.identity.name}
        </p>
        <ProfileUsername username={self.identity.username} badges={self.identity.badges ?? []}>
          {self.identity.pronouns !== null ? (
            <span className="rounded-full bg-cloud-100 px-2 py-0.5 text-[9px] font-bold text-cloud-600 ring-1 ring-cloud-200">
              {self.identity.pronouns}
            </span>
          ) : null}
          {self.identity.communityTag !== null ? (
            <span className="rounded bg-grape-100 px-1.5 py-0.5 text-[9px] font-bold text-grape-700 ring-1 ring-grape-200">
              {self.identity.communityTag.code}
            </span>
          ) : null}
        </ProfileUsername>
      </div>

      <div className="ui-glass-card mx-3 mt-3 rounded-xl border border-cloud-200 bg-cloud-50/70 p-3">
        <p className="text-xs font-semibold text-cloud-700">Make this profile yours</p>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => self.openSettings("profile")}
            className="ui-glass-button inline-flex items-center justify-center gap-1.5 rounded-lg border border-cloud-300 bg-white px-2 py-2 text-xs font-semibold text-cloud-700 transition-colors hover:bg-cloud-100"
          >
            <Edit3 className="h-3.5 w-3.5" />
            Edit profile
          </button>
          <button
            type="button"
            onClick={() => self.openSettings("appearance")}
            className="ui-glass-button inline-flex items-center justify-center gap-1.5 rounded-lg border border-cloud-300 bg-white px-2 py-2 text-xs font-semibold text-cloud-700 transition-colors hover:bg-cloud-100"
          >
            <Palette className="h-3.5 w-3.5" />
            Themes
          </button>
        </div>
      </div>

      <div className="mx-3 mb-3 mt-3 rounded-xl bg-grape-50/60 p-1.5 ring-1 ring-grape-100">
        <StatusPicker />
        <ActionRow
          icon={<LogOut className="h-4 w-4" />}
          label="Sign Out"
          onClick={() => {
            clearIdentityCache();
            void signOutSession().finally(() => window.location.reload());
          }}
        />
      </div>
    </>
  );
}
function StatusPicker() {
  const self = useSelf();
  const [open, setOpen] = useState(false);
  const current =
    STATUS_ORDER.find((o) => o.id === self.status) ?? STATUS_ORDER[0]!;
  const dotStatus =
    self.status === "invisible"
      ? "offline"
      : (self.status as "online" | "away" | "busy" | "offline");

  return (
    <div className="px-1 py-0.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm text-cloud-700 transition-colors hover:bg-white/70"
        aria-expanded={open}
      >
        <StatusDot
          status={dotStatus}
          sizeClass="h-2.5 w-2.5"
          ringClass="ring-white"
        />
        <span className="flex-1 truncate">
          <span className="text-[10px] uppercase tracking-wider text-cloud-400">
            Status ·{" "}
          </span>
          <span className="font-medium">{current.label}</span>
        </span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-cloud-400 transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <div className="mt-1 space-y-0.5 rounded-md bg-white p-1 ring-1 ring-grape-100">
          {STATUS_ORDER.map((opt) => {
            const active = self.status === opt.id;
            const optDot =
              opt.id === "invisible"
                ? "offline"
                : (opt.id as "online" | "away" | "busy" | "offline");
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => {
                  self.setStatus(opt.id);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                  active
                    ? "bg-strawberry-50 text-strawberry-700"
                    : "text-cloud-700 hover:bg-grape-50",
                )}
              >
                <StatusDot
                  status={optDot}
                  sizeClass="h-2.5 w-2.5"
                  ringClass="ring-white"
                />
                <span className="flex-1 truncate font-medium">{opt.label}</span>
                {opt.hint.length > 0 && (
                  <span className="hidden truncate text-[11px] text-cloud-400 sm:block">
                    {opt.hint}
                  </span>
                )}
                {active && (
                  <span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-strawberry-500" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ActionRow({
  icon,
  label,
  chevron,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  chevron?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-cloud-700 transition-colors hover:bg-white/70"
    >
      <span className="text-cloud-500">{icon}</span>
      <span className="flex-1 text-left">{label}</span>
      {chevron && <span className="text-cloud-400">›</span>}
    </button>
  );
}
